import axios from 'axios';
import { api } from './client';
import { AccountingCreateOrUpdateRequest } from '../types/requests/accounting.requests';
import { AccountingListResponse, AccountingSaveResponse } from '../types/responses/accounting.responses';
import { Accounting } from '../types/entities/accounting.entity';

export const accountingService = {
    getAll: async (page: number = 1, perPage: number = 15, filters: string = '', sort: string = '') => {
        let url = `/accountings?page=${page}&perPage=${perPage}`;
        if (filters) {
            url += `&${filters}`;
        }
        if (sort) {
            url += `&${sort}`;
        }
        const response = await api.get<AccountingListResponse>(url);
        return response.data.data;
    },

    create: async (accounting: AccountingCreateOrUpdateRequest): Promise<Accounting> => {
        const response = await api.post<AccountingSaveResponse>('/accountings', accounting);
        return response.data.data.accounting;
    },

    update: async (id: string, accounting: Partial<AccountingCreateOrUpdateRequest>): Promise<Accounting> => {
        const response = await api.put<AccountingSaveResponse>(`/accountings/${id}`, accounting);
        return response.data.data.accounting;
    },

    delete: async (id: string) => {
        await api.delete(`/accountings/${id}`);
    },

    generatePdf: async (): Promise<string | null> => {
        const response = await api.post('/accounting-pdf');
        return response.data.data?.url;
    },

    uploadAttachment: async (file: File): Promise<{ stored_path: string; original_name: string }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await api.post<{ status: string; data: { stored_path: string; original_name: string } }>(
            '/accounting-attachments',
            form,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            },
        );
        return response.data.data;
    },

    deleteStoredFile: async (storedPath: string): Promise<void> => {
        await api.delete('/accounting-attachments', { data: { path: storedPath } });
    },

    downloadAttachment: async (storedPath: string, originalName: string): Promise<void> => {
        const response = await api.get('/accounting-attachments/download', {
            params: { path: storedPath, name: originalName },
            responseType: 'blob',
        });
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalName || 'download';
        a.click();
        window.URL.revokeObjectURL(url);
    },

    /** For in-app preview (caller must revoke returned object URL when done). */
    fetchAttachmentBlob: async (storedPath: string, originalName: string): Promise<Blob> => {
        const response = await api.get('/accounting-attachments/download', {
            params: { path: storedPath, name: originalName },
            responseType: 'blob',
        });
        return response.data as Blob;
    },

    exportAttachmentsZip: async (filterQuery: string): Promise<void> => {
        const suffix = filterQuery ? `?${filterQuery}` : '';
        try {
            const response = await api.get(`/accountings/export-attachments${suffix}`, {
                responseType: 'blob',
                timeout: 300_000,
            });
            const blob = response.data as Blob;
            const contentType = String(response.headers['content-type'] ?? '');
            if (contentType.includes('application/json')) {
                const text = await blob.text();
                let message = 'Export failed';
                try {
                    const parsed = JSON.parse(text) as { message?: string };
                    if (parsed.message) {
                        message = parsed.message;
                    }
                } catch {
                    /* use default */
                }
                throw new Error(message);
            }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `accounting-attachments-${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err: unknown) {
            if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
                const text = await err.response.data.text();
                try {
                    const parsed = JSON.parse(text) as { message?: string };
                    if (parsed.message) {
                        throw new Error(parsed.message);
                    }
                } catch (inner) {
                    if (inner instanceof Error && inner.message !== text) {
                        throw inner;
                    }
                }
            }
            throw err;
        }
    },
};
